import React from 'react';
import { Modal } from './Slideover';
import { Button } from './Button';

/**
 * ConfirmationModal — the canonical confirm/destructive-action dialog.
 *
 * Replaces every `window.confirm(...)` call in the app (lint R5 = S1).
 * Built on the shared Modal primitive, so it inherits focus-trap, Esc-to-close,
 * backdrop dismiss, and the standard overlay chrome.
 *
 * Cancel = outlined secondary (surface bg, DARK ink ≥4.5:1 — never a ghost-grey
 * low-contrast button). Confirm = primary action, or `danger` tone for
 * destructive actions.
 *
 * Two ways to use it:
 *
 * 1) Controlled component:
 *      <ConfirmationModal
 *        open={open}
 *        title="Delete quote?"
 *        message="This can't be undone."
 *        confirmLabel="Delete"
 *        tone="danger"
 *        onConfirm={doDelete}
 *        onClose={() => setOpen(false)}
 *      />
 *
 * 2) Imperative hook (drop-in window.confirm replacement):
 *      const confirm = useConfirm();
 *      ...
 *      if (await confirm({ title: 'Delete quote?', tone: 'danger', confirmLabel: 'Delete' })) {
 *        doDelete();
 *      }
 *    (requires <ConfirmProvider/> mounted once near the app root)
 */
export default function ConfirmationModal({
  open,
  title = 'Are you sure?',
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary', // 'primary' | 'danger'
  busy = false,
  onConfirm,
  onClose,
}) {
  const confirmRef = React.useRef(null);

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      width="sm"
      initialFocus={confirmRef}
      closeOnBackdrop={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && (
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {message}
        </p>
      )}
      {children}
    </Modal>
  );
}

/* ------------------------------------------------------------------ *
 * Imperative confirm() — a Promise-based drop-in for window.confirm.
 * Mount <ConfirmProvider/> once; call useConfirm() anywhere below it.
 * ------------------------------------------------------------------ */
const ConfirmContext = React.createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = React.useState(null); // { opts, resolve }
  // §1 fix: keep the last opts while the exit transition runs so the
  // dialog doesn't flash the component defaults for two frames on close.
  const [lastOpts, setLastOpts] = React.useState(null);

  const confirm = React.useCallback((opts = {}) => {
    setLastOpts(opts);
    return new Promise((resolve) => setState({ opts, resolve }));
  }, []);

  const close = (result) => {
    state?.resolve(result);
    setState(null);
    // Don't clear lastOpts — let it linger until the next open. This keeps
    // the exit transition rendering the correct title/message/labels.
  };

  const opts = state?.opts || lastOpts || {};

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmationModal
        open={!!state}
        title={opts.title}
        message={opts.message}
        confirmLabel={opts.confirmLabel}
        cancelLabel={opts.cancelLabel}
        tone={opts.tone}
        onConfirm={() => close(true)}
        onClose={() => close(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a <ConfirmProvider>');
  }
  return ctx;
}
